<template>
  <div class="login-form-container">
    <v-card class="login-card" :loading="loading" elevation="0">
      <v-card-text class="px-8 py-6">
        <!-- Header with title and language selector -->
        <div class="form-header d-flex justify-space-between align-center mb-6">
          <h1 class="login-title-inline">{{ $t("login.loginTitle") }}</h1>
          <ChangeLanguage />
        </div>
        
        <v-form @submit.prevent="handleLogin" class="mt-2">
          <!-- Account Field -->
          <div class="form-group mb-6">
            <label class="field-label">{{ $t("router.account") }}</label>
            <div class="input-container">
              <v-text-field
                  :class="getUserInputClass()"
                  color="#4A90E2"
                  dense
                  outlined
                  v-model="email"
                  autocomplete="username email"
                  :placeholder="placeHolderEmail"
                  name="name"
                  class="custom-input"
                  hide-details="auto">
                <template v-slot:prepend-inner>
                  <v-icon color="#9CA3AF" size="20">mdi-account-outline</v-icon>
                </template>
              </v-text-field>
            </div>
            <div v-if="!validUser" class="text-valid-error mt-1">{{ validUserDetail }}</div>
          </div>

          <!-- Password Field -->
          <div class="form-group mb-6">
            <label class="field-label">{{ $t("login.password") }}</label>
            <div class="input-container">
              <v-text-field
                  :class="getPasswordInputClass()"
                  color="#4A90E2"
                  dense
                  outlined
                  :type="showPass"
                  v-model="password"
                  placeholder="••••••••"
                  autocomplete="current-password"
                  name="password"
                  class="custom-input"
                  hide-details="auto">
                <template v-slot:prepend-inner>
                  <v-icon color="#9CA3AF" size="20">mdi-lock-outline</v-icon>
                </template>
                <template v-slot:append>
                  <v-btn 
                      icon 
                      small 
                      @click="onShowPass" 
                      class="password-toggle">
                    <v-icon color="#9CA3AF" size="20">
                      {{ showPass === 'password' ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
                    </v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </div>
            <div v-if="!validPassword" class="text-valid-error mt-1">{{ validPasswordDetail }}</div>
          </div>

          <!-- Remember & Forgot Password -->
          <div class="form-options mb-8 d-flex align-center justify-space-between">
            <v-checkbox
                v-model="isRememberLogin"
                color="#4A90E2"
                class="remember-checkbox"
                hide-details
                dense>
              <template v-slot:label>
                <span class="checkbox-label">{{ rememberLoginInfo }}</span>
              </template>
            </v-checkbox>
            
            <v-btn
                text
                class="forgot-password-btn"
                @click="gotoLoginTab(1)">
              {{ forgotPassword }}
            </v-btn>
          </div>

          <!-- Login Button -->
          <v-btn
              type="submit"
              block
              large
              class="login-btn"
              :disabled="buttonLoginDisabled"
              :loading="isLoadingSubmit"
              color="#4A90E2">
            {{ login }}
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import i18n from "../../../lang/i18n";
import {API2} from "../../../api";
import storage, {STORAGE} from "../../../helper/storage";
import routes, {getDefaultPath} from "../../../routes";
import store from "../../../store";
import ChangeLanguage from "./ChangeLanguage.vue";
import path from "../../../routes/path";
import mixin from "../../../mixin";

export default {
  //TODO khoa account khi login sai 5 lan

  components: {ChangeLanguage},
  mixins: [mixin],
  props: {
    gotoLoginTab: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      email: "",
      password: "",
      isLoadingSubmit: false,
      validUser: true,
      validPassword: true,
      validUserDetail: "",
      validPasswordDetail: "",
      showPass: "password",
      isRememberLogin: false,
    };
  },
  computed: {
    placeHolderEmail: () => i18n.t("login.placeHolderEmail"),
    placeHolderPassword: () => i18n.t("login.placeHolderPassword"),
    login: () => i18n.t("login.login"),
    forgotPassword: () => i18n.t("login.forgotPassword"),
    rememberLoginInfo: () => i18n.t("login.rememberLoginInfo"),
    buttonLoginDisabled: function () {
      return !this.email || !this.password
    },
  },
  created() {
    // Auto-fill login form from remembered credentials
    this.loadRememberedCredentials();
  },
  methods: {
    loadRememberedCredentials() {
      // Load remembered credentials if they exist
      const rememberedEmail = storage.getItem(STORAGE.REMEMBERED_EMAIL);
      const rememberedPassword = storage.getItem(STORAGE.REMEMBERED_PASSWORD);
      
      if (rememberedEmail && rememberedPassword) {
        try {
          this.email = rememberedEmail;
          this.password = atob(rememberedPassword); // Base64 decode
          this.isRememberLogin = true;
        } catch (error) {
          // If decoding fails, clear the corrupted data
          storage.clearRememberedCredentials();
        }
      }
    },
    getEmailBlockStatus(email) {
      let statusRaw = storage.getItem(email)
      if (statusRaw) {
        let status = JSON.parse(statusRaw)
        status.block = status.time ? new Date(status.time).getTime() + 30 * 60 * 1000 > new Date().getTime() : false
        return status
      }
      return {failed: 0, block: false}
    },
    setEmailBlockStatus(email, status) {
      delete status.block
      return storage.setItem(email, JSON.stringify(status));
    },
    async handleLogin(evt) {
      if (evt) evt.preventDefault();

      let emailStatus = this.getEmailBlockStatus(this.email)
      if (emailStatus.block) {
        this.showToast('warning', i18n.t("ui.MSG0016"))
        return
      }

      let res = await this.post(API2.LOGIN, {
        email: this.email,
        password: this.password
      }, "isLoadingSubmit", false)

      if (res.data.status !== 200) {
        emailStatus.failed += 1
        if (emailStatus.failed === 5) {
          emailStatus.failed = 0
          emailStatus.time = new Date().toISOString()
        }
        this.setEmailBlockStatus(this.email, emailStatus)

        this.validUser = true
        this.validPassword = true
        let error = res.data.error
        switch (error.type) {
          case "account":
            this.validUser = false
            this.validUserDetail = error.message
            break;
          case "password":
            this.validPassword = false
            this.validPasswordDetail = error.message
            break;
          case "popup":
            this.showOverlay('warning', error.message, '', i18n.t("ui.tryAgain"),
                () => routes.push(path.login))
            break;
          case "toast":
            this.showToast('warning', error.message)
            break;
        }
        return
      }
      this.setEmailBlockStatus(this.email, {failed: 0})

      let token = res.data.data.token
      await store.dispatch("setToken", token);
      
      // Always save session data for current session
      storage.setItem(STORAGE.TOKEN, token);

      await this.getAccountInfo()
      this.isLoadingSubmit = false;

    },
    async getAccountInfo() {
      let account = await this.get(API2.INFO)
      if (!account) return

      // employee
      if (account.employeeId) {
        let listFunction = await this.get(API2.GET_USER_GRANT_FUNCTIONS)
        account.permission = listFunction?.functions?.reduce((acc, curr) => (acc[curr] = true, acc), {})
      }

      await store.dispatch("setAccount", account);
      
      // Always save session data for current session
      storage.setItem(STORAGE.ACCOUNT_INFO, JSON.stringify(account));
      
      // Handle "Remember me" functionality - save credentials for next login
      if (this.isRememberLogin) {
        storage.setItem(STORAGE.REMEMBERED_EMAIL, this.email);
        storage.setItem(STORAGE.REMEMBERED_PASSWORD, btoa(this.password)); // Base64 encode for basic obfuscation
      } else {
        // Clear remembered credentials if user unchecks "Remember me"
        storage.clearRememberedCredentials();
      }
      
      // Load user permissions for dynamic role system
      await store.dispatch("loadUserPermissions");

      let defaultPath = getDefaultPath(account)
      if (defaultPath === path.login) {
        return this.showToast('warning', i18n.t("ui.accessDennie"))
      }
      const permissionCount =  storage.getItem(STORAGE.USER_PERMISSIONS)?.length || 0;
      if(!permissionCount) {
          this.notifDialog({
          title: `${i18n.t("warning.warning")}!`,
          content: i18n.t("ui.noAccessPermissions"),
          textConfirm: i18n.t('ui.confirm'),
        });
        return;
      }
      await routes.push(defaultPath)
    },
    onShowPass() {
      this.showPass = this.showPass === "password" ? "text" : "password";
    },
    getUserInputClass() {
      return !this.validUser ? 'input-error' : ''
    },
    getPasswordInputClass() {
      return !this.validPassword ? 'input-error' : ''
    },
    clearAccount() {
      this.email = ''
      this.password = ''
    },
  },
};
</script>

<style scoped>
/* Container Styling */
.login-form-container {
  width: 100%;
  margin: 0 auto;
}

.login-card {
  background: #ffffff;
  border-radius: 16px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
}

.login-card >>> .v-card__text {
  padding: 2rem 1.5rem !important;
}

/* Form Header */
.form-header {
  margin-bottom: 2rem !important;
}

.login-title-inline {
  font-size: 28px;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

/* Language selector in form */
.form-header >>> .lang-dropdown .dropdown-toggle {
  color: #6B7280 !important;
  background-color: transparent !important;
  border: 1px solid #E5E7EB !important;
  border-radius: 6px !important;
  padding: 6px 12px !important;
  font-size: 14px !important;
}

.form-header >>> .lang-dropdown .dropdown-toggle:hover {
  border-color: #4A90E2 !important;
  color: #4A90E2 !important;
}

.form-header >>> .lang-dropdown .btn-secondary:not(:disabled):not(.disabled):active,
.form-header >>> .lang-dropdown .btn-secondary:not(:disabled):not(.disabled).active,
.form-header >>> .lang-dropdown .show > .btn-secondary.dropdown-toggle {
  color: #4A90E2 !important;
  background-color: transparent !important;
  border-color: #4A90E2 !important;
}

/* Form Group Styling */
.form-group {
  margin-bottom: 1.25rem;
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

/* Input Container */
.input-container {
  position: relative;
}

/* Custom Input Styling */
.custom-input >>> .v-input__control {
  min-height: 48px !important;
}

.custom-input >>> .v-text-field__details {
  display: none;
}

.custom-input >>> .v-input__slot {
  border-radius: 8px !important;
  background: #F9FAFB !important;
  border: 1px solid #E5E7EB !important;
  transition: all 0.2s ease;
}

.custom-input >>> .v-input__slot:hover {
  border-color: #4A90E2 !important;
  background: #ffffff !important;
}

.custom-input >>> .v-input__slot.v-input--is-focused {
  border-color: #4A90E2 !important;
  border-width: 2px !important;
  background: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1) !important;
}

.custom-input >>> .v-text-field__slot input {
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
}

.custom-input >>> .v-text-field__slot input::placeholder {
  color: #9CA3AF;
  font-size: 14px;
}

/* Error State */
.input-error >>> .v-input__slot {
  border-color: #EF4444 !important;
  background: #FEF2F2 !important;
}

.input-error >>> .v-input__slot:hover,
.input-error >>> .v-input__slot.v-input--is-focused {
  border-color: #EF4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

.text-valid-error {
  color: #EF4444;
  font-size: 12px;
  font-weight: 500;
}

/* Password Toggle */
.password-toggle {
  margin-right: 8px;
}

.password-toggle:hover {
  background-color: rgba(74, 144, 226, 0.1) !important;
}

/* Form Options */
.form-options {
  margin-bottom: 1.5rem;
}

/* Checkbox Styling */
.remember-checkbox >>> .v-input__slot {
  margin-bottom: 0 !important;
}

.remember-checkbox >>> .v-input {
  margin-bottom: 0 !important;
}

.remember-checkbox >>> .v-messages {
  display: none;
}

.remember-checkbox >>> .v-input__details {
  display: none;
  margin-bottom: 0 !important;
}

.remember-checkbox >>> .v-label {
  margin-bottom: 0 !important;
}

.remember-checkbox >>> .v-input--selection-controls__input {
  margin-right: 8px !important;
}

.remember-checkbox >>> .v-input--selection-controls__ripple {
  transform: scale(0.8);
}

.checkbox-label {
  font-size: 14px;
  color: #6B7280;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  margin: 0 !important;
  margin-bottom: 0 !important;
  padding: 0 !important;
  padding-bottom: 0 !important;
}

/* Forgot Password Button */
.forgot-password-btn {
  color: #4A90E2 !important;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  padding: 0 !important;
  margin: 0 !important;
  text-transform: none !important;
  height: auto !important;
  min-height: auto !important;
  line-height: 1.2;
}

.forgot-password-btn:hover {
  color: #3B82F6 !important;
  text-decoration: underline;
  background-color: transparent !important;
}

/* Login Button */
.login-btn {
  background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%) !important;
  border: 1px solid #4A90E2 !important;
  border-radius: 8px !important;
  height: 48px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.25) !important;
  transition: all 0.2s ease !important;
  color: #ffffff !important;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px) !important;
  box-shadow: 0 6px 20px rgba(74, 144, 226, 0.35) !important;
  background: linear-gradient(135deg, #357ABD 0%, #2968A3 100%) !important;
  border-color: #357ABD !important;
}

.login-btn:active:not(:disabled) {
  transform: translateY(0) !important;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.25) !important;
}

.login-btn.v-btn--disabled {
  background: #F3F4F6 !important;
  color: #9CA3AF !important;
  border: 1px solid #E5E7EB !important;
  box-shadow: none !important;
}

.login-btn >>> .v-btn__loader {
  color: #ffffff;
}

/* Responsive Design */
@media (max-width: 480px) {
  .login-form-container {
    margin: 0 16px;
    max-width: 320px;
  }
  
  .login-card >>> .v-card__text {
    padding: 1.5rem 1.25rem !important;
  }
  
  .field-label {
    font-size: 13px;
  }
  
  .form-options {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px;
    margin-bottom: 1.25rem;
  }
  
  .forgot-password-btn {
    align-self: flex-end;
    margin: 0 !important;
  }

  .login-title-inline {
    font-size: 24px;
  }
  
  .form-header >>> .lang-dropdown .dropdown-toggle {
    padding: 4px 8px !important;
    font-size: 12px !important;
  }
}

/* Animation for form elements */
.form-group {
  animation: slideInUp 0.3s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-group:nth-child(1) { animation-delay: 0.1s; }
.form-group:nth-child(2) { animation-delay: 0.2s; }
.form-options { animation: slideInUp 0.3s ease-out 0.3s both; }
.login-btn { animation: slideInUp 0.3s ease-out 0.4s both; }
</style>
